import {
  Accordion,
  Anchor,
  Code,
  Group,
  List,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title
} from '@mantine/core'
import { useSupabase } from '@packages/supabase'
import {
  IconExternalLink,
  IconKey,
  IconPlug,
  IconScript,
  IconWorld
} from '@tabler/icons-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DEFAULT_PUBLISHABLE_SCOPES } from '../types/integrationScopes'

const PARTNER_DEMO_DEV_URL = 'http://localhost:3011/partner-embed-demo.html'

export type TenantIntegrationsHowToProps = {
  i18nNamespace?: string
  translationKeyPrefix?: string
  embedBasePath?: string
  partnerDemoUrl?: string
}

export function TenantIntegrationsHowTo({
  i18nNamespace = 'ai',
  translationKeyPrefix = 'forms.integrations',
  embedBasePath = '/embed/forms',
  partnerDemoUrl = PARTNER_DEMO_DEV_URL
}: TenantIntegrationsHowToProps) {
  const { t } = useTranslation(i18nNamespace)
  const { apiURL } = useSupabase()
  const tk = (suffix: string, opts?: Record<string, unknown>) =>
    t(`${translationKeyPrefix}.howto.${suffix}`, opts)

  const formsOrigin =
    typeof window !== 'undefined' ? window.location.origin : ''
  const exchangeUrl = `${apiURL.replace(/\/$/, '')}/functions/v1/integration_exchange`
  const gatewayUrl = `${apiURL.replace(/\/$/, '')}/functions/v1/integration_gateway`
  const defaultScopes = DEFAULT_PUBLISHABLE_SCOPES.join(', ')
  const isDev = import.meta.env.DEV
  const submissionsPath = embedBasePath.replace(/\/$/, '') + '/submissions'

  const sdkExample = useMemo(() => {
    const base = formsOrigin || 'https://your-forms-app.example'
    const supa = apiURL.replace(/\/$/, '') || 'https://your-project.supabase.co'
    return `<script src="${base}/embed/v1/forms.js" async></script>
<div id="sc-forms-assistant"></div>
<script>
  StepCareForms.init({
    publishableKey: 'pk_test_…',
    baseUrl: '${base}',
    supabaseUrl: '${supa}',
    widgets: [{ mode: 'assistant', container: '#sc-forms-assistant' }],
    onSubmissionSaved: (id) => { /* partner hook */ }
  });
</script>`
  }, [apiURL, formsOrigin])

  return (
    <Paper withBorder p="md" radius="md" data-testid="tenant-integrations-howto">
      <Stack gap="sm">
        <Title order={5}>
          {tk('title', { defaultValue: 'How to embed Forms in your site' })}
        </Title>
        <Text size="sm" c="dimmed">
          {tk('subtitle', {
            defaultValue:
              'Use a publishable key for cross-origin partner portals. Session cookies only work when the iframe is on the same origin as this app.'
          })}
        </Text>

        <Accordion variant="separated" multiple defaultValue={['steps']}>
          <Accordion.Item value="steps">
            <Accordion.Control
              icon={
                <ThemeIcon size="sm" variant="light" color="teal">
                  <IconPlug size={14} />
                </ThemeIcon>
              }
            >
              {tk('steps_heading', { defaultValue: 'Step-by-step' })}
            </Accordion.Control>
            <Accordion.Panel>
              <List type="ordered" spacing="sm" size="sm">
                <List.Item>
                  {tk('step1', {
                    defaultValue:
                      'Create a publishable API key below. Add each partner site origin exactly (scheme + host + port).'
                  })}
                </List.Item>
                <List.Item>
                  {tk('step2', {
                    defaultValue: 'Grant at least these scopes: {{scopes}}.',
                    scopes: defaultScopes
                  })}
                </List.Item>
                <List.Item>
                  {tk('step3', {
                    defaultValue:
                      'From the partner page, POST to the exchange endpoint with your key and the partner Origin header.'
                  })}
                  <Code block mt="xs">
                    {exchangeUrl}
                  </Code>
                </List.Item>
                <List.Item>
                  {tk('step4', {
                    defaultValue:
                      'Load the embed with the returned access_token — use the SDK (recommended), iframe URL, or integration_gateway for JSON.'
                  })}
                  <List withPadding listStyleType="disc" mt="xs" size="sm">
                    <List.Item>
                      {tk('path_assistant', {
                        defaultValue: `Assistant: ${embedBasePath}?integration_token=…`
                      })}
                    </List.Item>
                    <List.Item>
                      {tk('path_submissions', {
                        defaultValue: `Submissions: ${submissionsPath}?integration_token=…`
                      })}
                    </List.Item>
                    <List.Item>
                      {tk('path_gateway', {
                        defaultValue: `JSON API: POST ${gatewayUrl}`
                      })}
                    </List.Item>
                  </List>
                </List.Item>
                <List.Item>
                  {tk('step5', {
                    defaultValue:
                      'Listen for postMessage events from this app origin: stepcare:embed_ready and stepcare:submission_saved.'
                  })}
                </List.Item>
              </List>
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="sdk">
            <Accordion.Control
              icon={
                <ThemeIcon size="sm" variant="light" color="teal">
                  <IconScript size={14} />
                </ThemeIcon>
              }
            >
              {tk('sdk_heading', { defaultValue: 'SDK snippet' })}
            </Accordion.Control>
            <Accordion.Panel>
              <Text size="xs" c="dimmed" mb="xs">
                {tk('sdk_hint', {
                  defaultValue:
                    'Replace pk_test_… with your key. The SDK calls exchange and mounts the iframe for you.'
                })}
              </Text>
              <Code block style={{ whiteSpace: 'pre-wrap' }}>
                {sdkExample}
              </Code>
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="dev">
            <Accordion.Control
              icon={
                <ThemeIcon size="sm" variant="light" color="teal">
                  <IconWorld size={14} />
                </ThemeIcon>
              }
            >
              {tk('dev_heading', { defaultValue: 'Local testing' })}
            </Accordion.Control>
            <Accordion.Panel>
              <List size="sm" spacing="xs">
                <List.Item>
                  {tk('dev_app', {
                    defaultValue:
                      'Run this Forms app (e.g. http://localhost:3010) and sign in here.'
                  })}
                </List.Item>
                <List.Item>
                  {tk('dev_hub', {
                    defaultValue:
                      'Run the Hub dev server (pnpm hub:dev) as a fake partner on http://localhost:3011.'
                  })}
                </List.Item>
                <List.Item>
                  {tk('dev_origin', {
                    defaultValue:
                      'When creating a key, allow origin http://localhost:3011.'
                  })}
                </List.Item>
              </List>
              {isDev && partnerDemoUrl && (
                <Anchor
                  href={partnerDemoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  mt="sm"
                  inline
                >
                  <Group gap={4} wrap="nowrap" component="span">
                    {tk('open_demo', {
                      defaultValue: 'Open Acme Care Portal demo'
                    })}
                    <IconExternalLink size={14} />
                  </Group>
                </Anchor>
              )}
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="security">
            <Accordion.Control
              icon={
                <ThemeIcon size="sm" variant="light" color="teal">
                  <IconKey size={14} />
                </ThemeIcon>
              }
            >
              {tk('security_heading', { defaultValue: 'Security notes' })}
            </Accordion.Control>
            <Accordion.Panel>
              <List size="sm" spacing="xs">
                <List.Item>
                  {tk('security_publishable', {
                    defaultValue:
                      'Publishable keys are safe in the browser only with an origin allowlist.'
                  })}
                </List.Item>
                <List.Item>
                  {tk('security_secret', {
                    defaultValue:
                      'Secret keys are for server-side automation only — never put them in HTML or mobile apps.'
                  })}
                </List.Item>
                <List.Item>
                  {tk('security_ttl', {
                    defaultValue:
                      'Exchange tokens expire quickly; call exchange again instead of storing long-lived tokens.'
                  })}
                </List.Item>
              </List>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Stack>
    </Paper>
  )
}
